/**
 * Para onde cada bloco leva. A Visão executiva responde a pergunta com um número, e a tela
 * que mostra as linhas por trás dele já existe: os links abrem ELA no mesmo recorte, por
 * query (`/ops?risco=...`), que é como os filtros das telas são lidos.
 *
 * Os nomes dos parâmetros vêm das telas de destino (`parametrosDoMapa`, `opsOrdem`), nunca
 * escritos de novo aqui: um link que inventa o nome do filtro leva para a tela certa sem
 * recorte nenhum, e isso não quebra em lugar nenhum, só mente.
 */
import { MAPA_TAB_SEMANA } from '@/app/mapa/helpers/abasDoMapa';
import { MAPA_CELULA_PARAM, MAPA_SEMANA_PARAM } from '@/app/mapa/helpers/parametrosDoMapa';
import { ORDEM_FORA_DO_PRAZO, ORDEM_KEY } from '@/app/ops/helpers/opsOrdem';
import { ACTIVE_TAB_PARAM } from '@/domain/common/hooks/useActiveTabParam';

/** A Carteira e o Mapa, as duas telas que respondem com a lista por trás dos números. */
const CARTEIRA = '/ops';
const MAPA = '/mapa';

/**
 * A própria Visão executiva, que é uma ABA do Início e não uma rota. Quem manda para cá é
 * a rota antiga `/visao-executiva`, mantida de pé só para não quebrar link já enviado.
 */
export const VISAO_EXECUTIVA_HREF = `/?${ACTIVE_TAB_PARAM}=executiva`;

/** Um caminho com o recorte, pelo mesmo encode que a barra de endereço usa. */
function comQuery(rota: string, params: Record<string, string>): string {
  const query = new URLSearchParams(params).toString();
  return query === '' ? rota : `${rota}?${query}`;
}

/** A Carteira inteira, na aba da carteira: o rodapé dos blocos que falam dela. */
export const CARTEIRA_HREF = CARTEIRA;

/** A Carteira aberta em "Prazo: Em risco", o recorte do bloco do risco. */
export const CARTEIRA_EM_RISCO_HREF = comQuery(CARTEIRA, { risco: 'EM_RISCO' });

/** O Mapa na grade POR SEMANA, que é a leitura dos blocos de capacidade. */
export const MAPA_POR_SEMANA_HREF = comQuery(MAPA, { [ACTIVE_TAB_PARAM]: MAPA_TAB_SEMANA });

/**
 * O Mapa › Por semana filtrado por uma célula. Com um dia (a semana em que a célula fica
 * livre) o detalhe já abre a partir da semana dele; sem dia, ele abre onde abriria.
 */
export function mapaDaCelulaHref(celula: string, dia?: string | null): string {
  const recorte: Record<string, string> = {
    [ACTIVE_TAB_PARAM]: MAPA_TAB_SEMANA,
    [MAPA_CELULA_PARAM]: celula,
  };
  if (dia !== undefined && dia !== null && dia !== '') {
    recorte[MAPA_SEMANA_PARAM] = dia;
  }
  return comQuery(MAPA, recorte);
}

/**
 * A Carteira de uma célula com o que NÃO sai no prazo em cima: é a leitura que o bloco de
 * pontualidade abre, porque a pergunta dele é justamente quais OPs estouram o prazo ali.
 */
export function carteiraDaCelulaHref(celula: string): string {
  return comQuery(CARTEIRA, { celula, [ORDEM_KEY]: ORDEM_FORA_DO_PRAZO });
}

/** A Carteira filtrada por uma marca, o destino de uma linha dos blocos de marca. */
export function carteiraDaMarcaHref(marca: string): string {
  return comQuery(CARTEIRA, { marca });
}

/**
 * A Carteira filtrada por VÁRIAS marcas de uma vez, que é o que "Outras" abre: ela não é
 * uma marca, é o resto delas. A Carteira lê a lista separada por vírgula (`readList`),
 * então o recorte chega inteiro.
 */
export function carteiraDeVariasMarcasHref(marcas: Array<string>): string {
  return comQuery(CARTEIRA, { marca: marcas.join(',') });
}
